import { useCallback, useMemo, useRef } from 'react';

const PRELOAD_MAX_AGE = 5 * 60 * 1000; // 5 mins
const TIME_TO_INTENT = 200;

export type OnLoad<T> = (obj: { dispose: () => void; reference: T }) => void;

export type UsePressablePreloadRefProps<T> = {
	load: () => T;
	loadOnFocus?: boolean;
	onBeforeLoad?: () => void;
	onLoad: OnLoad<T>;
};

export function usePressablePreloadRefNew<T extends { dispose: () => void }>({
	load,
	onLoad,
	onBeforeLoad,
	loadOnFocus = false,
}: UsePressablePreloadRefProps<T>) {
	const nodeRef = useRef<HTMLElement | null>(null);

	const { clickListener, mouseDownListener, preloadReference, cancelPreload } = useMemo(() => {
		const request: {
			load?: { dispose: () => void; reference: T };
			preload?: { dispose: () => void; reference: T };
			preloadIntentTimeout?: ReturnType<typeof setTimeout>;
			preloadMaxAgeTimeout?: ReturnType<typeof setTimeout>;
		} = {};

		const clearPreloadTimeouts = () => {
			if (typeof request.preloadIntentTimeout === 'number') {
				clearTimeout(request.preloadIntentTimeout);
				delete request.preloadIntentTimeout;
			}

			if (typeof request.preloadMaxAgeTimeout === 'number') {
				clearTimeout(request.preloadMaxAgeTimeout);
				delete request.preloadMaxAgeTimeout;
			}
		};

		const cancelPreload = () => {
			clearPreloadTimeouts();
			request.preload?.dispose();
		};

		const loadReference = () => {
			if (request.load) {
				return request.load;
			}

			const reference = request.preload?.reference ?? load();
			if (request.preload) {
				delete request.preload;
			}

			request.load = {
				reference,
				dispose() {
					reference.dispose();
					delete request.load;
				},
			};

			return request.load;
		};

		const clickListener = () => {
			clearPreloadTimeouts();
			if (onBeforeLoad) {
				onBeforeLoad();
			}
			onLoad(loadReference());
		};

		const mouseDownListener = () => {
			clearPreloadTimeouts();
			loadReference();
		};

		const preloadReference = () => {
			if (request.preloadIntentTimeout || request.preload) {
				return;
			}

			request.preloadIntentTimeout = setTimeout(() => {
				delete request.preloadIntentTimeout;

				const reference = load();

				request.preload = {
					reference,
					dispose() {
						reference.dispose();
						delete request.preload;
					},
				};

				request.preloadMaxAgeTimeout = setTimeout(() => {
					delete request.preloadMaxAgeTimeout;
					request.preload?.dispose();
				}, PRELOAD_MAX_AGE);
			}, TIME_TO_INTENT);
		};

		return {
			cancelPreload,
			clickListener,
			mouseDownListener,
			preloadReference,
		};
	}, [load, onBeforeLoad, onLoad]);

	const refCallback = useCallback(
		(element: HTMLElement | null) => {
			if (element !== nodeRef.current) {
				if (nodeRef.current) {
					cancelPreload();
					nodeRef.current.removeEventListener('click', clickListener);
					nodeRef.current.removeEventListener('mousedown', mouseDownListener);
					nodeRef.current.removeEventListener('mouseenter', preloadReference);
					nodeRef.current.removeEventListener('mouseleave', cancelPreload);
					if (loadOnFocus) {
						nodeRef.current.removeEventListener('focusin', clickListener);
					}
				}
				if (element) {
					element.addEventListener('click', clickListener);
					element.addEventListener('mousedown', mouseDownListener);

					element.addEventListener('mouseenter', preloadReference);
					element.addEventListener('mouseleave', cancelPreload);

					if (loadOnFocus) {
						element.addEventListener('focusin', clickListener);
					}
				}
				// Update the ref with the new element
				nodeRef.current = element;
			}
		},
		[clickListener, mouseDownListener, preloadReference, cancelPreload, loadOnFocus],
	);

	return refCallback;
}
